import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, queryKeys } from '@/app/utils/constants';
import { CheckListIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainScrollArea } from '@/app/ui/components/main';
import { useGetDependenciesQuery } from '@/app/components/features/evaluations/analysis/Dependencies/useDependencies';
import { DependencyDetailNew } from '@/app/components/features/evaluations/Dependencies/DependencyDetailNew';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { getEPADetailsQuery } from '@/app/hooks/queries/useEarlyPhaseAssessment';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/early-phase-assessments/$earlyPhaseAssessmentId/_layout/(main)/dependencies/$dependencyId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.epaHome');
  const tFeature = useTranslations('components.dependencies');
  const { earlyPhaseAssessmentId, projectId, dependencyId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getEPADetailsQuery(earlyPhaseAssessmentId));

  const { data: allDependencies } = useGetDependenciesQuery({
    earlyPhaseAssessmentId,
  });

  const allDependenciesIds = useMemo(() => {
    return allDependencies?.map((dep) => ({ id: dep.id })) || [];
  }, [allDependencies]);

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allDependenciesIds, dependencyId, (dependencyId) => ({
      to: '/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/dependencies/$dependencyId',
      params: { projectId, earlyPhaseAssessmentId, dependencyId },
    }));

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                <HugeiconsIcon icon={CheckListIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/early-phase-assessments/$earlyPhaseAssessmentId/dependencies"
                params={{ projectId, earlyPhaseAssessmentId }}
              >
                {tFeature('header')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        <DependencyDetailNew
          evaluationId={earlyPhaseAssessmentId}
          id={dependencyId}
          queryKeysToInvalidate={[
            [queryKeys.DEPENDENCY, earlyPhaseAssessmentId, dependencyId],
            [queryKeys.DEPENDENCIES, earlyPhaseAssessmentId],
          ]}
        />
      </MainScrollArea>
    </>
  );
}
